<!--
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
-->

<div class="sample-detail">
  <!-- Download Section -->
  <div class="download-section">
    <h3>Download Project Archive</h3>
    <p>Complete source code project with integration examples:</p>
    <a href="/samples/javascript-integration/Athena.Auth.Javascript.zip" class="download-btn" download>
      ⬇️ Athena.Auth.Javascript.zip
    </a>
  </div>

  <!-- Overview Section -->
  <section class="section-overview">
    <h3>Project Overview</h3>
    <p>
      This project demonstrates how to implement integration of <strong>Microsoft Authentication Library (MSAL)</strong>
      for user authentication and authorization when accessing <strong>Athena API</strong>.
      Athena serves as a centralized API gateway providing secure access to various backend services,
      including IoT devices, GCP products, and data visualization services.
    </p>

    <h4>Application Features:</h4>
    <ul>
      <li>✓ <strong>User Authentication</strong> via Azure AD B2C using MSAL</li>
      <li>✓ <strong>Access Token Acquisition</strong> for Athena API authorization</li>
      <li>✓ <strong>Authenticated Requests Execution</strong> to various backend services through Athena</li>
      <li>✓ <strong>Token Lifecycle Management</strong>, including automatic refresh and error handling</li>
    </ul>
  </section>

  <!-- Project Structure Section -->
  <section class="section-structure">
    <h3>Project Structure</h3>
    <div class="code-block-light">
      <pre><code>├── index.html                 # Main demonstration page
├── package.json              # Project dependencies and scripts
├── css/
│   └── styles.css           # Application styles
├── js/
│   └── app.js               # Main application logic
├── lib/
│   ├── athena-auth.js       # MSAL wrapper for Athena authentication
│   └── athena-service.js    # API service for Athena requests
└── config/
    └── athena-config.js     # Athena configuration</code></pre>
    </div>
  </section>

  <!-- Core Services Section -->
  <section class="section-services">
    <h3>Core Services</h3>

    <!-- Configuration Service -->
    <div class="service-card">
      <h4>1. Athena Configuration (config/athena-config.js)</h4>
      <p>The configuration service manages all authentication and API parameters required for Athena integration:</p>

      <h5>Key Parameters:</h5>
      <ul>
        <li><strong>Azure AD B2C Configuration:</strong> Client ID, Tenant ID for authentication</li>
        <li><strong>API Configuration:</strong> Base endpoint, subscription key, required scopes</li>
        <li><strong>MSAL Options:</strong> Token caching, cookie storage parameters</li>
        <li><strong>Redirect Configuration:</strong> Managing redirects after authentication</li>
      </ul>

      <h5>Configuration Example:</h5>
      <div class="code-block" ngNonBindable>
        <pre><code>export const athenaAuthConfig = &#123;
    // Azure AD B2C Configuration
    clientId: "your-client-id",
    tenantId: "your-tenant-id",

    // Athena API Configuration
    baseEndpoint: "https://ath-dataplane-cu-dev-apim.azure-api.net/api",
    subscriptionKey: "your-subscription-key",
    apiScopes: [
        'openid',
        'profile',
        'https://b2cdaikindev.onmicrosoft.com/.../athena.access'
    ],

    // Application Configuration
    redirectUri: 'http://localhost:4200',
    msalOptions: &#123;
        cache: &#123;
            cacheLocation: "localStorage",
            storeAuthStateInCookie: false
        &#125;
    &#125;
&#125;;</code></pre>
      </div>
    </div>

    <!-- Authentication Service -->
    <div class="service-card">
      <h4>2. Athena Authentication (lib/athena-auth.js)</h4>
      <p>The authentication service provides a comprehensive <strong>MSAL wrapper</strong> that simplifies Microsoft authentication:</p>

      <h5>Key MSAL Features:</h5>
      <ul>
        <li><strong>Popup/Redirect Authentication:</strong> Support for both authentication methods</li>
        <li><strong>Silent Token Acquisition:</strong> Automatic token refresh with fallback to interactive mode</li>
        <li><strong>Account Management:</strong> Active account tracking and profile access</li>
        <li><strong>Event-Driven Architecture:</strong> Callbacks for login, logout, errors, and token events</li>
      </ul>

      <h5>Main Methods:</h5>
      <div class="code-block" ngNonBindable>
        <pre><code>// Authentication lifecycle
await athenaAuth.login();                    // User authentication
const isAuthenticated = athenaAuth.isAuthenticated(); // Check authentication status
const profile = athenaAuth.getUserProfile(); // Get user information
await athenaAuth.logout();                   // User logout

// Token management
const token = await athenaAuth.getAccessToken(); // Get access token
const account = athenaAuth.getActiveAccount();   // Get current account

// Event handlers
athenaAuth.onLogin((account) =&gt; &#123; /* handle login */ &#125;);
athenaAuth.onError((error) =&gt; &#123; /* handle errors */ &#125;);
athenaAuth.onUserLogout(() =&gt; &#123; /* handle logout */ &#125;);</code></pre>
      </div>
    </div>

    <!-- API Service -->
    <div class="service-card">
      <h4>3. Athena API Service (lib/athena-service.js)</h4>
      <p>The request handler service executes all HTTP requests to Athena with automatic authentication and error handling:</p>

      <h5>Key Features:</h5>
      <ul>
        <li><strong>Automatic Authentication:</strong> Ensures authentication before API requests</li>
        <li><strong>Token Management:</strong> Automatically includes Bearer tokens and subscription keys</li>
        <li><strong>Request/Response Interceptors:</strong> Extensible middleware for request processing</li>
        <li><strong>HTTP Method Support:</strong> GET, POST, PUT, DELETE, PATCH operations</li>
        <li><strong>Error Handling:</strong> Comprehensive error handling with detailed information</li>
        <li><strong>Event Callbacks:</strong> Processing events throughout the request lifecycle</li>
      </ul>
    </div>
  </section>

  <!-- Authorization Section -->
  <section class="section-authorization">
    <h3>How to Authorize Requests</h3>

    <!-- Authentication Flow -->
    <div class="subsection">
      <h4>Authentication Flow</h4>

      <h5>1. Services Initialization</h5>
      <div class="code-block" ngNonBindable>
        <pre><code>import &#123; AthenaAuth &#125; from './lib/athena-auth.js';
import &#123; AthenaService &#125; from './lib/athena-service.js';
import &#123; athenaAuthConfig &#125; from './config/athena-config.js';

const athenaAuth = new AthenaAuth(athenaAuthConfig);
const athenaService = new AthenaService(athenaAuthConfig, athenaAuth);</code></pre>
      </div>

      <h5>2. User Authentication</h5>
      <div class="code-block" ngNonBindable>
        <pre><code>// Authenticate user (popup method)
await athenaAuth.login();

// Check authentication
if (athenaAuth.isAuthenticated()) &#123;
    console.log("User successfully authenticated");
&#125;</code></pre>
      </div>

      <h5>3. Executing Authenticated API Requests through Athena</h5>
      <p>
        The <code>AthenaService</code> automatically handles:
      </p>
      <ul>
        <li>User authentication verification</li>
        <li>Access token acquisition and refresh</li>
        <li>Authorization headers (Bearer token)</li>
        <li>API subscription key headers</li>
        <li>Base URL construction</li>
      </ul>

      <h5>API Request Examples:</h5>
      <div class="code-block" ngNonBindable>
        <pre><code>// Get IoT devices
try &#123;
    const devices = await athenaService.get('devices');
    console.log('Devices retrieved:', devices);
&#125; catch (error) &#123;
    console.error('API request failed:', error);
&#125;

// Get device points with parameters
const devicePoints = await athenaService.get('device-points', &#123;
    deviceId: 'SOLU2001P00002',
    sourceId: '3101057'
&#125;);

// Get product details (POST request)
const productDetails = await athenaService.post('product/details', &#123;
    data: &#123;
        productName: "wme-c"
    &#125;
&#125;);

// Get data points from visualization service
const dataPoints = await athenaService.get('data-points');</code></pre>
      </div>
    </div>
  </section>

  <!-- Getting Started Section -->
  <section class="section-getting-started">
    <h3>Getting Started</h3>

    <h4>Prerequisites</h4>
    <ul>
      <li>Node.js and npm installed</li>
      <li>Modern web browser with ES6+ support</li>
      <li>Valid Athena credentials and subscription key</li>
    </ul>

    <h4>Installation and Setup</h4>
    <ol>
      <li>
        <strong>Install Dependencies</strong>
        <div class="code-block">
          <pre><code>npm install</code></pre>
        </div>
      </li>
      <li>
        <strong>Configure Authentication</strong>
        <p>Update <code>config/athena-config.js</code> with your Athena credentials:</p>
        <ul>
          <li>Client ID and Tenant ID from Azure AD B2C</li>
          <li>Athena API base endpoint and subscription key</li>
          <li>Required API scopes for your application</li>
        </ul>
      </li>
      <li>
        <strong>Run Development Server</strong>
        <div class="code-block">
          <pre><code>npm start</code></pre>
        </div>
        <p>The application will be available at <code>http://localhost:4200</code></p>
      </li>
    </ol>

    <h4>Development Scripts</h4>
    <div class="code-block">
      <pre><code>npm start        # Run development server with CORS support
npm run serve    # Run server with additional security headers</code></pre>
    </div>
  </section>

  <!-- API Examples Section -->
  <section class="section-api-examples">
    <h3>API Request Examples</h3>

    <h4>Complete Request Flow</h4>
    <div class="code-block">
      <pre><code>// 1. Initialize authentication and services
const athenaAuth = new AthenaAuth(athenaAuthConfig);
const athenaService = new AthenaService(athenaAuthConfig, athenaAuth);

// 2. Set event handlers
athenaAuth.onLogin((account) =&gt; &#123;
    console.log("Authentication successful");
&#125;);

athenaService.onSuccess((data, response) =&gt; &#123;
    console.log("API request successful:", response.status);
&#125;);

// 3. Authenticate user
await athenaAuth.login();

// 4. Execute API requests
const devices = await athenaService.get('devices');</code></pre>
    </div>

    <h4>Request Headers</h4>
    <p>All requests to Athena automatically include:</p>
    <div class="code-block" ngNonBindable>
      <pre><code>Authorization: Bearer &#123;&#123;access_token&#125;&#125;
Content-Type: application/json
X-Subscription-Key: &#123;&#123;subscription_key&#125;&#125;</code></pre>
    </div>

    <h4>Event Handlers</h4>
    <p>The library provides several event handlers for various authentication states:</p>
    <div class="code-block" ngNonBindable>
      <pre><code>// Successful login
athenaAuth.onLogin((account) =&gt; &#123;
    // Handle successful login
&#125;);

// Errors (login, token, API)
athenaAuth.onError((error) =&gt; &#123;
    // Handle authentication errors
&#125;);

// Logout
athenaAuth.onUserLogout(() =&gt; &#123;
    // Handle user logout
&#125;);

// Token acquisition
athenaAuth.onToken((token) =&gt; &#123;
    // Handle token acquisition
&#125;);</code></pre>
    </div>
  </section>

  <!-- Benefits Section -->
  <section class="section-benefits">
    <h3>Architecture Benefits</h3>
    <ul>
      <li><strong>Separation of Concerns:</strong> Configuration separated from logic</li>
      <li><strong>Reusability:</strong> Athena authentication can be used in multiple projects</li>
      <li><strong>Maintainability:</strong> Easier to update and maintain authentication logic</li>
      <li><strong>Flexibility:</strong> Easy to configure without changing code</li>
      <li><strong>Error Handling:</strong> Centralized error handling and event management</li>
      <li><strong>Modular Architecture:</strong> Clean separation between auth, service, and configuration layers</li>
    </ul>
  </section>

  <!-- Support Section -->
  <section class="section-support">
    <h3>Support</h3>
    <p>
      If you encounter issues with Athena API integration or authentication,
      please refer to the <a href="/api-mesh/samples#getting-started">API Mesh documentation</a> or contact the API Mesh support team (<a href="mailto:Athena_Support&#64;daacloud.onmicrosoft.com">Athena_Support&#64;daacloud.onmicrosoft.com</a>).
    </p>
  </section>
</div>
